"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Tagline } from "@/components/pro-blocks/landing-page/tagline";
import { cn } from "@/lib/utils";

interface StatsItem {
  title: string;
  value: string;
  description: string;
}

interface StatsSectionProps {
  tagline?: string;
  title?: string;
  titleTag?: "h1" | "h2" | "h3";
  titleClassName?: string;
  description?: string;
  stats?: StatsItem[];
  colorMode?: "light" | "bright" | "dark" | "midnight";
}

export function StatsSection({
  tagline,
  title,
  titleTag = "h2",
  titleClassName = "heading-lg",
  description,
  stats,
  colorMode = "light",
}: StatsSectionProps) {
  const TitleTag = titleTag;
  const isBrightMode = colorMode === "bright";
  const isDarkMode = colorMode === "dark";
  const isMidnightMode = colorMode === "midnight";
  const isDarkTone = isDarkMode || isMidnightMode;
  const titleLines = (title ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const hasStats = Boolean(stats?.length);
  const hasHeadingContent = Boolean(
    tagline || titleLines.length || description,
  );

  if (!hasStats && !hasHeadingContent) return null;

  return (
    <section className="bg-background section-padding-y">
      <div className="container-padding-x container mx-auto">
        <div className="flex flex-col gap-10 md:gap-12">
          <div className="gap-2 flex lg:max-w-lg 3xl:max-w-3xl flex-col">
            {tagline ? <Tagline>{tagline}</Tagline> : null}
            {titleLines.length ? (
              <TitleTag className={cn(titleClassName, "text-foreground")}>
                {titleLines.map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </TitleTag>
            ) : null}
            {description ? (
              <p className={cn(titleClassName, "text-muted-foreground")}>
                {description}
              </p>
            ) : null}
          </div>

          {hasStats ? (
            <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
              {stats?.map((stat) => (
                <Card
                  key={`${stat.title}-${stat.value}`}
                  className={cn(
                    "p-4 md:p-6 lg:flex-1",
                    isMidnightMode
                      ? "border-white/10 bg-[#0D0F33]"
                      : isDarkMode
                        ? "border-white/10 bg-foreground"
                        : isBrightMode
                          ? "bg-card"
                          : "bg-[#f1f0ee]",
                  )}
                >
                  <CardContent className="flex h-full flex-col gap-2 p-0 md:gap-3">
                    <h3
                      className={cn(
                        "text-base font-semibold",
                        isDarkTone ? "text-white" : "text-foreground",
                      )}
                    >
                      {stat.title}
                    </h3>
                    <span
                      className={cn(
                        "text-3xl font-geist md:text-4xl",
                        isDarkTone ? "text-white" : "text-foreground",
                      )}
                    >
                      {stat.value}
                    </span>
                    <p
                      className={cn(
                        "text-base",
                        isDarkTone ? "text-white/72" : "text-muted-foreground",
                      )}
                    >
                      {stat.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
